import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Coffee } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const from = location.state?.from?.pathname || '/mon-compte';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError('');
    setEmailModalOpen(false);

    try {
      const result = await signIn(formData.email, formData.password);

      if (result.success) {
        toast.success('Connexion réussie !');
        navigate(from, { replace: true });
        return;
      }

      if (result.emailConfirmationRequired) {
        setEmailModalOpen(true);
        return;
      }

      setAuthError(result.error || 'Erreur de connexion');
    } catch (error) {
      setAuthError('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <main
        data-testid="login-page"
        className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center py-16"
      >
        <div className="w-full max-w-lg px-6">
          <div className="bg-white p-8 lg:p-10 border border-border-subtle">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center justify-center mb-6">
                <div className="w-14 h-14 rounded-full bg-latte-gold/10 flex items-center justify-center mr-3">
                  <Coffee className="w-7 h-7 text-latte-gold" />
                </div>
                <span className="font-serif text-2xl font-semibold text-espresso">
                  Léger's <span className="text-latte-gold">Coffee</span>
                </span>
              </Link>

              <h1 className="font-serif text-2xl lg:text-3xl text-espresso font-semibold mb-2">
                Connexion
              </h1>

              <p className="text-soft-charcoal-secondary">
                Accédez à votre espace client
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-soft-charcoal font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="input-field h-14 text-base"
                  placeholder="votre@email.com"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-soft-charcoal font-medium">
                    Mot de passe
                  </Label>
                  <Link
                    to="/mot-de-passe-oublie"
                    className="text-sm text-latte-gold hover:underline"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>

                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="input-field h-14 text-base pr-12"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-soft-charcoal-secondary hover:text-espresso"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {authError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {authError}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary h-14 text-base"
                data-testid="login-submit"
              >
                {isLoading ? 'Connexion...' : 'Se Connecter'}
              </Button>
            </form>

            <div className="mt-8 pt-8 border-t border-border-subtle text-center">
              <p className="text-soft-charcoal-secondary mb-3">
                Pas encore de compte ?
              </p>
              <Link
                to="/inscription"
                className="inline-flex items-center text-espresso font-medium hover:text-latte-gold transition-colors duration-300"
                data-testid="register-link"
              >
                Créer un compte
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email non validé</DialogTitle>
            <DialogDescription>
              Vous devez valider votre adresse email avant de pouvoir vous connecter.
              Vérifiez votre boîte mail puis cliquez sur le lien de confirmation.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button onClick={() => setEmailModalOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
